import { LitElement, html, css } from 'lit'
import { focusTrap } from '../../lib/focus-trap'
import $ from 'quantdom'

export class DropdownButton extends LitElement {
  static styles = [
    css`
      :host {
        display: block;
      }

      slot[name='button'] {
        cursor: pointer;
      }

      .dropdown-container {
        position: relative;
        width: 100%;
      }

      .dropdown-menu {
        opacity: 0;
      }
      slot[aria-expanded='true'] + .dropdown-menu {
        opacity: 1;
      }
    `,
  ]

  static properties = {
    active: { type: Boolean, reflect: true },
    selectedProduct: { type: String, reflect: true },
  }

  connectedCallback() {
    super.connectedCallback()
    this.active = false
    this.button = this.renderRoot.querySelector('[name="button"]')

    this.focusTrap = new focusTrap({
      el: this,
      includeActiveElement: false,
      escCallback: () => {
        this._toggle()
      },
    })

    this.windowClickHandler = (e) => {
      let dropdownComponent = this
      let dropdownMenuEl = this.querySelector('[slot="ul"]')

      if (
        !(dropdownComponent == e.target || dropdownComponent.contains(e.target)) &&
        !(e.target.nodeName === 'DROPDOWN-BUTTON')
      ) {
        this._toggle()
      }

      if (dropdownMenuEl.contains(e.target)) {
        this._toggle()
      }
    }
  }

  firstUpdated() {
    this.querySelector('ul').classList.add('loaded')
  }

  render() {
    return html`
      <div class="dropdown-container">
        <slot
          name="button"
          aria-expanded=${this.active}
          @click=${this._onClick}
          @keydown=${this._buttonKeydownHandler}
        ></slot>
        <div class="dropdown-menu" hidden>
          <slot name="ul" @keydown=${this._liKeydownHandler}>
            <slot name="li"></slot>
          </slot>
        </div>
      </div>
    `
  }

  _onClick(e) {
    const isDisabled = e.target.closest('dropdown-button').getAttribute('aria-disabled')
    if (isDisabled === 'true') return
    this._toggle()
  }

  _buttonKeydownHandler(e) {
    switch (e.key) {
      case 'ArrowDown':
        if (this.active === false) {
          this._toggle()
        }
        this._selectFirstLi(e)
        break
      case 'ArrowUp':
        this._selectLastLi(e)
        break
      case 'Escape':
        this._toggle()
        break
    }

    this._updateActiveDescendant()
    this._updateAriaSelected()
  }

  _liKeydownHandler(e) {
    switch (e.keyCode) {
      case 40:
        // arrow down
        this._selectNextLi(e)
        break
      case 38:
        // arrow up
        this._selectPreviousLi(e)
        break
      case 32:
        // space or enter
        e.preventDefault()
        e.target.click()
        break
      case 13:
        // enter
        e.target.click()
        this._toggle()
        break
      case 27:
        // escape
        this._toggle()
        break
    }
    this._updateActiveDescendant()
    this._updateAriaSelected()
  }

  _selectNextLi(e) {
    e.preventDefault()
    let nextElement = e.target.nextElementSibling
    if (nextElement && nextElement.classList.contains('selected')) {
      nextElement = nextElement.nextElementSibling
    }

    if (nextElement) {
      nextElement.focus()
    }
  }

  _selectPreviousLi(e) {
    e.preventDefault()
    let previousElement = e.target.previousElementSibling
    if (previousElement && previousElement.classList.contains('selected')) {
      previousElement = previousElement.previousElementSibling
    }
    if (previousElement) {
      previousElement.focus()
    }
  }

  _selectFirstLi(e) {
    e.preventDefault()
    let firstElement = e.target.parentElement.querySelector('li:not(.selected)')
    if (firstElement) {
      firstElement.focus()
    }
  }

  _selectLastLi(e) {
    e.preventDefault()
    let lastElement = e.target.parentElement.querySelectorAll('li:not(.selected)')
    lastElement = lastElement[lastElement.length - 1]
    if (lastElement) {
      lastElement.focus()
    }
  }

  _updateActiveDescendant() {
    const activeDescendant = this.querySelector('li:focus')?.id

    if (activeDescendant) {
      this.button.setAttribute('aria-activedescendant', activeDescendant)
    } else {
      this.button.removeAttribute('aria-activedescendant')
    }
  }

  _updateAriaSelected() {
    const focusedLi = this.querySelector('li:focus')
    const listItems = this.querySelectorAll('li')
    for (var i = 0; i < listItems.length; i++) {
      if (listItems[i] === focusedLi) {
        listItems[i].setAttribute('aria-selected', 'true')
      } else {
        listItems[i].removeAttribute('aria-selected')
      }
    }
  }

  _toggle() {
    this.menu = this.shadowRoot.querySelector('.dropdown-menu')
    this.button = this.renderRoot.querySelector('[name="button"]').assignedElements()[0]
    this.active = !this.active
    this.button.setAttribute('aria-expanded', this.active)
    if (this.active) {
      this.focusTrap.activate()
      this.menu.removeAttribute('hidden')
      window.addEventListener('click', this.windowClickHandler)
    } else {
      this.focusTrap.deactivate()
      this.menu.setAttribute('hidden', 'true')
      window.removeEventListener('click', this.windowClickHandler)
    }
  }
}

customElements.define('dropdown-button', DropdownButton)
